/**
 * Argus — WS client singleton pentru pattern „command direct prin WS".
 *
 * Folosit ca alternativă la flow-ul REST (emitCommand + poll status + fetch artifact).
 * Inițial introdus pentru `dir_listing` în blocul `ws-direct-dir-listing`.
 * Bloc ws-direct-batch-2 2026-05-05: extins pentru screenshot, enumerate_devices, file_read.
 *
 * API:
 *   await wsClient.requestDirListing(agentId, path, opts) -> DirListingResult
 *   await wsClient.requestScreenshot(agentId, justification, displayName?) -> ScreenshotResult
 *   await wsClient.requestEnumerateDevices(agentId) -> EnumDevicesResult
 *   await wsClient.requestFileRead(agentId, path, justification) -> FileReadResult
 *
 * Conexiune lazy on first request, reconnect automat (backoff exponential),
 * cleanup la `wsClient.close()` (apelat la logout).
 */
import { getOperatorWsUrl } from './streams';

const REQUEST_TIMEOUT_MS = 30_000;
const RECONNECT_DELAYS_MS = [1_000, 2_000, 5_000, 10_000];

export class WsDisconnectedError extends Error {
  constructor() { super('WS connection lost'); this.name = 'WsDisconnectedError'; }
}
export class WsTimeoutError extends Error {
  constructor() { super('WS request timed out'); this.name = 'WsTimeoutError'; }
}
export class WsAgentOfflineError extends Error {
  constructor(message?: string) { super(message ?? 'Agent offline'); this.name = 'WsAgentOfflineError'; }
}
export class WsPermissionDeniedError extends Error {
  constructor(message?: string) { super(message ?? 'Permission denied'); this.name = 'WsPermissionDeniedError'; }
}
export class WsCommandFailureError extends Error {
  readonly errorCode: string;
  constructor(errorCode: string, message?: string) {
    super(message ?? `WS command failure: ${errorCode}`);
    this.name = 'WsCommandFailureError';
    this.errorCode = errorCode;
  }
}
/** Bloc ws-direct-batch-2 — file_read >10 MB. Caller cade pe REST fallback. */
export class WsFileTooLargeError extends Error {
  readonly errorCode = 'FILE_TOO_LARGE_FOR_WS';
  readonly size: number;
  readonly thresholdBytes: number;
  constructor(size: number, thresholdBytes: number) {
    super(`Fișier prea mare pentru WS direct (size=${size}, max=${thresholdBytes})`);
    this.name = 'WsFileTooLargeError';
    this.size = size;
    this.thresholdBytes = thresholdBytes;
  }
}

export interface DirListingEntry {
  readonly name: string;
  readonly relativePath: string;
  readonly type: 'file' | 'dir';
  readonly size: number | null;
  readonly modifiedAt: string;
  readonly isHidden: boolean;
  readonly isReadOnly: boolean;
  readonly driveType?: string | null;
  readonly freeBytes?: number | null;
  readonly totalBytes?: number | null;
  readonly volumeLabel?: string | null;
  readonly iconKey?: string | null;
  readonly volumeSerial?: number | null;
  readonly fileId?: string | null;
}

export interface IconBundleSizes {
  readonly '20': string;
  readonly '48': string;
  readonly '96': string;
}

export interface IconBundle {
  readonly hash: string;
  readonly sizes: IconBundleSizes;
}

export interface DirListingResult {
  readonly path: string;
  readonly recursive: boolean;
  readonly maxDepth: number;
  readonly capturedAt: string;
  readonly totalEntries: number;
  readonly totalErrors: number;
  readonly truncated: boolean;
  readonly entries: readonly DirListingEntry[];
  readonly errors: readonly { relativePath: string; code: string; message: string }[];
  /** Catalog iconițe per iconKey (bloc ws-icons-inline 2026-05-05). */
  readonly icons?: Record<string, IconBundle>;
}

export interface RequestDirListingOptions {
  readonly recursive?: boolean;
  readonly maxDepth?: number;
}

// === Bloc ws-direct-batch-2 — types pentru screenshot, enumerate_devices, file_read ===

export interface ScreenshotResult {
  readonly png: string; // base64
  readonly width: number;
  readonly height: number;
  readonly capturedAt: string;
  readonly displayName: string;
}

export interface CameraPictureResult {
  readonly jpeg: string; // base64
  readonly width: number;
  readonly height: number;
  readonly capturedAt: string;
  readonly cameraName: string;
}

export interface ShowMessageResult {
  readonly dismissed: boolean;
  readonly dismissedAt: string;
}

export interface Resolution {
  readonly width: number;
  readonly height: number;
  readonly fps: number;
}

export interface CameraInfo {
  readonly deviceId: string;
  readonly name: string;
  readonly supportedResolutions: readonly Resolution[];
  readonly defaultResolution: Resolution | null;
}

export interface MicrophoneInfo {
  readonly deviceId: string;
  readonly name: string;
  readonly defaultSampleRate: number;
  readonly defaultChannels: number;
}

export interface EnumDevicesResult {
  readonly cameras: readonly CameraInfo[];
  readonly microphones: readonly MicrophoneInfo[];
}

export interface FileReadResult {
  readonly content: string; // base64
  readonly mimeType: string;
  readonly size: number;
  readonly fileName: string;
  readonly modifiedAt: string;
}

/** Bloc ws-direct-batch-3 — chunk pentru streaming video + bulkDownload chunked. */
export interface FileChunkResult {
  readonly data: string;       // base64
  readonly offset: number;
  readonly size: number;       // actual bytes returned (poate fi < size cerut la eof)
  readonly totalSize: number;
  readonly eof: boolean;
  readonly mimeType: string;
  readonly modifiedAt: string;
}

/** Bloc ws-direct-thumbnails 2026-05-09 — thumbnail extracted via WS direct (DB cache populated fire-and-forget). */
export interface ThumbnailResult {
  readonly fileHash: string;
  readonly pngBase64: string;
  readonly mimeType: string;     // tipic 'image/png'
}

// Bloc recording-browser-pivot 2026-05-09: types `RecordingStartResult` + `RecordingStartOpts`
// dropped — recording = browser-side (vezi `lib/browserRecorder.ts`), nu mai trece prin WS.

interface PendingEntry {
  resolve: (data: unknown) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

function generateCommandId(): string {
  // UUID v7-ish: timestamp ms (48 bit) + random. Hex 32 chars uppercase.
  // Format aliniat cu PHP `uuidV7Bytes`.
  const ts = Date.now();
  const tsHi = Math.floor(ts / 0x10000);
  const tsLo = ts & 0xFFFF;
  const rand = crypto.getRandomValues(new Uint8Array(10));
  // Set version 7 (high nibble of byte 6) and variant (high bits of byte 8).
  rand[0] = ((rand[0] ?? 0) & 0x0F) | 0x70;
  rand[2] = ((rand[2] ?? 0) & 0x3F) | 0x80;
  const hex = (n: number, w: number) => n.toString(16).padStart(w, '0');
  return (
    hex(tsHi, 8) +
    hex(tsLo, 4) +
    Array.from(rand, (b) => hex(b, 2)).join('')
  ).toUpperCase();
}

class WSClient {
  private ws: WebSocket | null = null;
  private closed = false;
  private reconnectAttempts = 0;
  private connectPromise: Promise<void> | null = null;
  private readonly pending = new Map<string, PendingEntry>();
  // Dedup in-flight requests pentru același (agentId, path, recursive, maxDepth) — bloc
  // ws-icons-inline 2026-05-05. Caller-ii multipli (FolderTreeView + FileExplorerWindow.useQuery)
  // primesc același Promise → un singur WS round-trip.
  private readonly inflightDirListing = new Map<string, Promise<DirListingResult>>();
  private readonly inflightScreenshot = new Map<string, Promise<ScreenshotResult>>();
  private readonly inflightEnumDevices = new Map<string, Promise<EnumDevicesResult>>();
  private readonly inflightFileRead = new Map<string, Promise<FileReadResult>>();

  async requestDirListing(
    agentId: string,
    path: string,
    opts: RequestDirListingOptions = {}
  ): Promise<DirListingResult> {
    const recursive = opts.recursive ?? false;
    const maxDepth = opts.maxDepth ?? 1;
    const inflightKey = `${agentId.toUpperCase()}|${path}|${recursive}|${maxDepth}`;
    const existing = this.inflightDirListing.get(inflightKey);
    if (existing) return existing;

    const promise = this.requestCommand<DirListingResult>(agentId, 'dir_listing', { path, recursive, maxDepth });
    this.inflightDirListing.set(inflightKey, promise);
    try {
      return await promise;
    } finally {
      this.inflightDirListing.delete(inflightKey);
    }
  }

  /** Bloc ws-direct-batch-2 — screenshot transient (NO disk artifact, NO DB). */
  async requestScreenshot(
    agentId: string,
    justification: string,
    displayName?: string,
  ): Promise<ScreenshotResult> {
    const inflightKey = `${agentId.toUpperCase()}|${displayName ?? ''}`;
    const existing = this.inflightScreenshot.get(inflightKey);
    if (existing) return existing;

    const params: Record<string, unknown> = { justification };
    if (displayName !== undefined) params.displayName = displayName;
    const promise = this.requestCommand<ScreenshotResult>(agentId, 'screenshot', params);
    this.inflightScreenshot.set(inflightKey, promise);
    try {
      return await promise;
    } finally {
      this.inflightScreenshot.delete(inflightKey);
    }
  }

  /**
   * Send Message — popup MessageBox pe user desktop (UserHelper P/Invoke MessageBoxW).
   * Blochează thread agent până user dismiss. Promise rezolvă DUPĂ click OK.
   * Timeout 10 minute (defensive — dacă utilizator nu vede popup-ul/AFK).
   */
  async requestSendMessage(
    agentId: string,
    body: string,
    title: string,
    justification: string,
  ): Promise<ShowMessageResult> {
    const params: Record<string, unknown> = { body, title, justification };
    return this.requestCommand<ShowMessageResult>(agentId, 'show_message', params);
  }

  /**
   * Bloc v1.0.10 #B — set_watched_paths WS direct (refactor din REST POST PHP).
   * Frontend → WS → Node (DB transaction direct: DELETE/INSERT watched_paths + INSERT commands)
   * → Agent (FsWatcherService.ApplyConfigAsync) → result success/failure.
   */
  async requestSetWatchedPaths(
    agentId: string,
    paths: ReadonlyArray<{ path: string; recursive: boolean; eventTypes: ReadonlyArray<string>; extensionsWhitelist?: ReadonlyArray<string>; extensionsBlacklist?: ReadonlyArray<string> }>,
    justification: string,
  ): Promise<{ ok: true }> {
    const params: Record<string, unknown> = { paths, justification };
    return this.requestCommand<{ ok: true }>(agentId, 'set_watched_paths', params);
  }

  /**
   * Quick Webcam button — single frame max-res JPEG via ffmpeg subprocess pe agent.
   * Pure WS direct (NO REST, NO DB persist). Returnează JPEG base64 inline.
   */
  async requestCameraPicture(
    agentId: string,
    cameraName: string,
    width: number,
    height: number,
    justification: string,
  ): Promise<CameraPictureResult> {
    const params: Record<string, unknown> = { cameraName, width, height, justification };
    return this.requestCommand<CameraPictureResult>(agentId, 'camera_picture', params);
  }

  /** Bloc ws-direct-batch-2 — enumerate cameras+mics cu rezoluții per cameră. */
  async requestEnumerateDevices(agentId: string): Promise<EnumDevicesResult> {
    const inflightKey = agentId.toUpperCase();
    const existing = this.inflightEnumDevices.get(inflightKey);
    if (existing) return existing;

    const promise = this.requestCommand<EnumDevicesResult>(agentId, 'enumerate_devices', {});
    this.inflightEnumDevices.set(inflightKey, promise);
    try {
      return await promise;
    } finally {
      this.inflightEnumDevices.delete(inflightKey);
    }
  }

  /**
   * Bloc ws-direct-batch-3 — chunk pentru streaming video + bulkDownload >10 MB.
   * NU dedup in-flight (chunks distinct per offset). Default size 1 MB.
   */
  async requestFileChunk(
    agentId: string,
    path: string,
    offset: number,
    size: number,
    justification?: string,
  ): Promise<FileChunkResult> {
    const params: Record<string, unknown> = { path, offset, size };
    if (justification !== undefined) params.justification = justification;
    return this.requestCommand<FileChunkResult>(agentId, 'file_read_chunk', params);
  }

  /**
   * Bloc ws-direct-thumbnails 2026-05-09 — request thumbnail extraction prin WS direct.
   * Agent UserHelper ExtractToBytesAsync via SHIL_JUMBO (picture) sau ffmpeg seek (video).
   * Timeout 35s — video extraction poate dura 5-10s ffmpeg seek-schedule.
   * Node fire-and-forget POST la PHP /internal/cache-thumbnail pentru DB persist.
   */
  async requestThumbnail(
    agentId: string,
    fileHash: string,
    absPath: string,
    mtime: number,
    extKind: 'picture' | 'video',
  ): Promise<ThumbnailResult> {
    return this.requestCommandWithTimeout<ThumbnailResult>(
      agentId,
      'thumbnail_request',
      { fileHash, absPath, mtime, extKind },
      35_000,
    );
  }

  /**
   * Bloc ws-direct-batch-2 — file_read ≤10 MB inline. La FILE_TOO_LARGE_FOR_WS aruncă
   * WsFileTooLargeError → caller cade pe REST fallback.
   */
  async requestFileRead(
    agentId: string,
    path: string,
    justification: string,
  ): Promise<FileReadResult> {
    const inflightKey = `${agentId.toUpperCase()}|${path}`;
    const existing = this.inflightFileRead.get(inflightKey);
    if (existing) return existing;

    const promise = this.requestCommand<FileReadResult>(
      agentId, 'file_read', { path, justification },
    );
    this.inflightFileRead.set(inflightKey, promise);
    try {
      return await promise;
    } finally {
      this.inflightFileRead.delete(inflightKey);
    }
  }

  /**
   * Bloc file-push-deployment 2026-05-07 — init transfer (open file handle pe agent, validate path).
   * Caller (lib/filePush.ts) primește ack ok=true, apoi loop chunks.
   */
  async requestFilePushInit(
    agentId: string,
    init: { localId: string; targetPath: string; fileName: string; sizeBytes: number },
  ): Promise<{ localId: string; ok: true }> {
    return this.requestCommand<{ localId: string; ok: true }>(agentId, 'file_push_init', init);
  }

  /** Bloc file-push-deployment 2026-05-07 — chunk write. eof=true → agent rename atomic + emite file_pushed. */
  async requestFilePushChunk(
    agentId: string,
    chunk: { localId: string; offset: number; data: string; eof: boolean },
  ): Promise<{ localId: string; offset: number; bytesWritten: number; eofProcessed: boolean }> {
    return this.requestCommand<{ localId: string; offset: number; bytesWritten: number; eofProcessed: boolean }>(
      agentId, 'file_push_chunk', chunk,
    );
  }

  // Bloc task-manager-services 2026-05-07 — sysadmin power tools.
  async requestProcessList(agentId: string): Promise<{
    processes: ReadonlyArray<{
      pid: number; parentPid: number; name: string; ramBytes: number;
      sessionId: number; userName: string | null; startTime: string | null;
    }>;
    capturedAt: string;
  }> {
    return this.requestCommand(agentId, 'process_list', {});
  }

  async requestProcessKill(agentId: string, pid: number, force = false): Promise<{ ok: true }> {
    return this.requestCommand(agentId, 'process_kill', { pid, force });
  }

  async requestServicesList(agentId: string): Promise<{
    services: ReadonlyArray<{
      name: string; displayName: string; status: string; startType: string;
      account: string; description: string | null;
    }>;
    capturedAt: string;
  }> {
    return this.requestCommand(agentId, 'services_list', {});
  }

  async requestServiceControl(
    agentId: string,
    name: string,
    action: 'start' | 'stop' | 'restart',
  ): Promise<{ ok: true }> {
    return this.requestCommand(agentId, `service_${action}`, { name });
  }

  // Bloc remote-powershell-shell 2026-05-08 — admin terminal cu streaming chunks.
  // Acceptă explicit commandId pentru ca UI să poată asocia chunks cu execuția activă.
  // Promise rezolvă la final result; chunks vin separate via CustomEvent argus:shell-chunk.
  async requestShellExec(
    agentId: string,
    commandId: string,
    command: string,
    timeoutSeconds = 30,
  ): Promise<{ exitCode: number; durationMs: number; truncated: boolean; totalBytes: number }> {
    return this.requestCommandWithExplicitId(
      agentId, commandId, 'shell_exec', { command, timeoutSeconds },
      Math.max(35_000, (timeoutSeconds + 5) * 1000),
    );
  }

  async requestShellCancel(agentId: string, targetCommandId: string): Promise<{ ok: true }> {
    return this.requestCommand(agentId, 'shell_cancel', { targetCommandId });
  }

  // Bloc recording-browser-pivot 2026-05-09 — methods `requestRecordingStart` + `requestRecordingStop`
  // dropped. Recording = browser-side via MediaRecorder (vezi `lib/browserRecorder.ts`).

  // Bloc 2026-05-10 webrtc-pivot — signaling pentru camera streaming smooth via SIPSorcery agent.
  async requestWebRtcInit(
    agentId: string,
    streamId: string,
    cameraId: string,
    stunUrl?: string,
    microphoneId?: string,
    turn?: { urls: string; username: string; credential: string },
    codec?: 'vp8' | 'h264',
  ): Promise<{ streamId: string; sdp: string }> {
    // Bloc webrtc-audio-support 2026-05-11: microphoneId opţional pentru audio track simultan.
    // Bloc turn-server-secure-creds 2026-05-11: turn opţional pentru TURN relay auth HMAC.
    // Bloc webcam-modal-toolbar-redesign 2026-05-12: codec opţional (vp8 default backward-compat).
    const params: Record<string, string> = { streamId, cameraId, stunUrl: stunUrl ?? '' };
    if (microphoneId) params.microphoneId = microphoneId;
    if (turn && turn.urls && turn.username && turn.credential) {
      params.turnUrl = turn.urls;
      params.turnUsername = turn.username;
      params.turnCredential = turn.credential;
    }
    if (codec) params.codec = codec;
    return this.requestCommand(agentId, 'webrtc_init', params);
  }
  async requestWebRtcAnswer(agentId: string, streamId: string, sdp: string): Promise<{ ok: true }> {
    return this.requestCommand(agentId, 'webrtc_answer', { streamId, sdp });
  }
  async requestWebRtcIce(agentId: string, streamId: string, candidate: RTCIceCandidateInit): Promise<{ ok: true }> {
    return this.requestCommand(agentId, 'webrtc_ice', { streamId, candidate });
  }
  async requestWebRtcClose(agentId: string, streamId: string): Promise<{ ok: true }> {
    return this.requestCommand(agentId, 'webrtc_close', { streamId });
  }

  // Bloc ws-direct-stream-control 2026-05-10 — stream_start / stream_stop migrate la WS direct.
  // ws-node generează streamId server-side (UUID v7) + INSERT stream_sessions. Răspunsul include
  // streamId în data (prin server inject; agent nu cunoaște streamId pentru a-l returna în reply).
  async requestStreamStart(
    agentId: string,
    params: Record<string, unknown>,
  ): Promise<{ streamId: string; status: string; expiresAt: string | null }> {
    return this.requestCommand(agentId, 'stream_start', params);
  }

  async requestStreamStop(
    agentId: string,
    streamId: string,
    justification: string = 'Manual stream stop',
  ): Promise<{ streamId: string; status: string }> {
    return this.requestCommand(agentId, 'stream_stop', { streamId, justification });
  }

  /** Bloc remote-powershell-shell — variant cu commandId explicit (caller-provided, UI-trackable). */
  private async requestCommandWithExplicitId<TResult>(
    agentId: string,
    commandId: string,
    command: string,
    params: Record<string, unknown>,
    timeoutMs: number,
  ): Promise<TResult> {
    await this.ensureConnected();
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      throw new WsDisconnectedError();
    }
    const msg = { type: 'command_request', commandId, agentId: agentId.toUpperCase(), command, params };
    return new Promise<TResult>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(commandId);
        reject(new WsTimeoutError());
      }, timeoutMs);
      this.pending.set(commandId, {
        resolve: (data) => resolve(data as TResult),
        reject,
        timer,
      });
      try {
        ws.send(JSON.stringify(msg));
      } catch (err) {
        this.pending.delete(commandId);
        clearTimeout(timer);
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    });
  }

  /** Generic helper — un round-trip WS request-response cu commandId correlation. */
  private async requestCommand<TResult>(
    agentId: string,
    command: string,
    params: Record<string, unknown>,
  ): Promise<TResult> {
    return this.requestCommandWithTimeout<TResult>(agentId, command, params, REQUEST_TIMEOUT_MS);
  }

  /** Bloc remote-powershell-shell 2026-05-08 — variant cu timeout custom (long-running shell exec). */
  private async requestCommandWithTimeout<TResult>(
    agentId: string,
    command: string,
    params: Record<string, unknown>,
    timeoutMs: number,
  ): Promise<TResult> {
    await this.ensureConnected();
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      throw new WsDisconnectedError();
    }

    const commandId = generateCommandId();
    const msg = {
      type: 'command_request',
      commandId,
      agentId: agentId.toUpperCase(),
      command,
      params,
    };

    return new Promise<TResult>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(commandId);
        reject(new WsTimeoutError());
      }, timeoutMs);
      this.pending.set(commandId, {
        resolve: (data) => resolve(data as TResult),
        reject,
        timer,
      });
      try {
        ws.send(JSON.stringify(msg));
      } catch (err) {
        this.pending.delete(commandId);
        clearTimeout(timer);
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    });
  }

  /** Bloc remote-powershell-shell 2026-05-08 — last commandId trimisă (pentru cancel cu UI commandId tracking). */
  generateLocalCommandId(): string {
    return generateCommandId();
  }

  /**
   * Connect eager (înainte de prima request) — folosit pentru a primi push-uri server (USB events, etc.).
   * Apelat din RequireAuth la primul mount post-auth.
   */
  async connectEager(): Promise<void> {
    try {
      await this.ensureConnected();
    } catch {
      // Eșec inițial — reconnect logic va încerca din nou la prima request.
    }
  }

  /** Forțat reconnect — folosit la logout pentru a reseta state. */
  close(): void {
    this.closed = true;
    if (this.ws) {
      try { this.ws.close(1000, 'Client logout'); } catch { /* ignore */ }
      this.ws = null;
    }
    for (const [, entry] of this.pending) {
      clearTimeout(entry.timer);
      entry.reject(new WsDisconnectedError());
    }
    this.pending.clear();
  }

  private ensureConnected(): Promise<void> {
    if (this.closed) this.closed = false; // re-init după logout/login
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return Promise.resolve();
    if (this.connectPromise) return this.connectPromise;

    this.connectPromise = new Promise<void>((resolve, reject) => {
      const url = getOperatorWsUrl();
      let ws: WebSocket;
      try {
        ws = new WebSocket(url);
      } catch (err) {
        this.connectPromise = null;
        reject(err instanceof Error ? err : new Error(String(err)));
        return;
      }
      this.ws = ws!;

      ws.addEventListener('open', () => {
        this.reconnectAttempts = 0;
        this.connectPromise = null;
        resolve();
      });

      ws.addEventListener('message', (ev) => {
        if (typeof ev.data !== 'string') return;
        try {
          const msg = JSON.parse(ev.data) as { type?: string };
          if (msg?.type === 'command_result') {
            this.onCommandResult(msg as Record<string, unknown>);
          } else if (msg?.type === 'device_event_push') {
            // Bloc continuous-monitoring-usb 2026-05-06 — broadcast la consumers via CustomEvent
            window.dispatchEvent(new CustomEvent('argus:device-event', { detail: msg }));
          } else if (msg?.type === 'agent_status_push') {
            // Bloc agent-status-realtime-push 2026-05-06 — broadcast la consumers via CustomEvent
            window.dispatchEvent(new CustomEvent('argus:agent-status', { detail: msg }));
          } else if (msg?.type === 'agent_thumbnail_push') {
            // Bloc desktop-thumbnail-ws 2026-05-06 — agent push thumbnail PNG inline.
            window.dispatchEvent(new CustomEvent('argus:agent-thumbnail', { detail: msg }));
          } else if (msg?.type === 'agent_metrics_live') {
            // Bloc v1.0.9 #2 — 1Hz live metrics push de la agent (separat de heartbeat 10s persist DB).
            window.dispatchEvent(new CustomEvent('argus:agent-metrics-live', { detail: msg }));
          } else if (msg?.type === 'agent_version_push') {
            // Bloc 2026-05-06 — server detectează schimbare versiune post-update în heartbeat.
            window.dispatchEvent(new CustomEvent('argus:agent-version', { detail: msg }));
          } else if (msg?.type === 'admin_alert') {
            // Bloc auto-blacklist v1.0.5 — server alertează operatori la auto-blacklist manifest.
            window.dispatchEvent(new CustomEvent('argus:admin-alert', { detail: msg }));
          } else if (msg?.type === 'file_pushed') {
            // Bloc file-push-deployment 2026-05-07 — agent emite post-completion fișier (eof rename atomic).
            // Consumer: FileExplorerWindow refresh fsEntriesCache pentru parentPath dacă match currentPath.
            window.dispatchEvent(new CustomEvent('argus:file-pushed', { detail: msg }));
          } else if (msg?.type === 'shell_chunk_push') {
            // Bloc remote-powershell-shell 2026-05-08 — agent streaming stdout/stderr chunks.
            window.dispatchEvent(new CustomEvent('argus:shell-chunk', { detail: msg }));
          } else if (msg?.type === 'webrtc_local_ice') {
            // Bloc 2026-05-10 webrtc-pivot — agent push ICE candidate per stream.
            window.dispatchEvent(new CustomEvent('argus:webrtc-ice', { detail: msg }));
          }
          // Other types (subscribe_ack, stream_terminated, etc.) ignored — handled by other consumers.
        } catch {
          // Malformed JSON — ignore.
        }
      });

      ws.addEventListener('close', () => {
        this.ws = null;
        this.connectPromise = null;
        // Reject all pending — re-emit by caller if needed.
        for (const [, entry] of this.pending) {
          clearTimeout(entry.timer);
          entry.reject(new WsDisconnectedError());
        }
        this.pending.clear();
        // Schedule reconnect IF not closed by client.
        if (!this.closed) {
          const delay = RECONNECT_DELAYS_MS[Math.min(this.reconnectAttempts, RECONNECT_DELAYS_MS.length - 1)];
          this.reconnectAttempts++;
          setTimeout(() => {
            // Re-attempt connect lazily when next request fires.
            void this.ensureConnected().catch(() => { /* will retry */ });
          }, delay);
        }
      });

      ws.addEventListener('error', () => {
        // close event will fire afterwards.
      });
    });

    return this.connectPromise;
  }

  private onCommandResult(msg: Record<string, unknown>): void {
    const commandId = typeof msg.commandId === 'string' ? msg.commandId.toUpperCase() : '';
    const entry = this.pending.get(commandId);
    if (!entry) return;
    this.pending.delete(commandId);
    clearTimeout(entry.timer);

    const status = typeof msg.status === 'string' ? msg.status : 'failure';
    if (status === 'success') {
      entry.resolve(msg.data);
      return;
    }
    const errorCode = typeof msg.errorCode === 'string' ? msg.errorCode : 'UNKNOWN';
    const errorMessage = typeof msg.errorMessage === 'string' ? msg.errorMessage : undefined;
    if (errorCode === 'AGENT_OFFLINE') {
      entry.reject(new WsAgentOfflineError(errorMessage));
    } else if (errorCode === 'PERMISSION_DENIED') {
      entry.reject(new WsPermissionDeniedError(errorMessage));
    } else if (errorCode === 'FILE_TOO_LARGE_FOR_WS') {
      const meta = msg.meta as { size?: number; thresholdBytes?: number } | undefined;
      entry.reject(new WsFileTooLargeError(meta?.size ?? 0, meta?.thresholdBytes ?? 10_485_760));
    } else {
      entry.reject(new WsCommandFailureError(errorCode, errorMessage));
    }
  }
}

export const wsClient = new WSClient();
